import React from 'react';
import { AppleOutlined, ExportOutlined, SpotifyOutlined } from '@ant-design/icons';
import { Space, Typography } from 'antd';
import {
    appleMusicArtistUrl,
    formatArtistLastRelease,
    spotifyArtistUrl,
} from './artistStoreLinks';

const { Text } = Typography;

type ArtistSearchOptionProps = {
    artist: API.MusicDistribution.ArtistSearchResult;
};

const StoreRow: React.FC<{
    storeLabel: React.ReactNode;
    artistName: string;
    lastRelease: string;
    profileUrl?: string;
}> = ({ storeLabel, artistName, lastRelease, profileUrl }) => (
    <div
        style={{
            display: 'grid',
            gridTemplateColumns: '88px 1fr minmax(120px, 1.2fr) 28px',
            gap: 8,
            alignItems: 'center',
            padding: '4px 0',
            borderTop: '1px solid #f0f0f0',
            fontSize: 12,
        }}
    >
        <Space size={4}>{storeLabel}</Space>
        <Text ellipsis>{artistName}</Text>
        <Text type="secondary" ellipsis title={lastRelease}>
            {lastRelease}
        </Text>
        {profileUrl ? (
            <a
                href={profileUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                aria-label="Open store artist page"
            >
                <ExportOutlined />
            </a>
        ) : (
            <span />
        )}
    </div>
);

const ArtistSearchOption: React.FC<ArtistSearchOptionProps> = ({ artist }) => {
    const lastRelease = formatArtistLastRelease(artist.last_release);
    const spotifyId = artist.spotify_id?.trim();
    const appleId = artist.apple_music_id?.trim();
    const hasStoreRow = Boolean(spotifyId || appleId);

    return (
        <div style={{ padding: '4px 0', width: '100%' }}>
            <Text strong style={{ display: 'block', marginBottom: hasStoreRow ? 4 : 0 }}>
                {artist.key_name}
            </Text>
            {hasStoreRow && (
                <div style={{ marginTop: 4 }}>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '88px 1fr minmax(120px, 1.2fr) 28px',
                            gap: 8,
                            padding: '0 0 4px',
                            fontSize: 11,
                            color: 'var(--text-muted)',
                        }}
                    >
                        <span>Store</span>
                        <span>Name</span>
                        <span>Latest release</span>
                        <span />
                    </div>
                    {spotifyId && (
                        <StoreRow
                            storeLabel={
                                <>
                                    <SpotifyOutlined style={{ color: '#1db954' }} />
                                    <span>Spotify</span>
                                </>
                            }
                            artistName={artist.key_name}
                            lastRelease={lastRelease}
                            profileUrl={spotifyArtistUrl(spotifyId)}
                        />
                    )}
                    {appleId && (
                        <StoreRow
                            storeLabel={
                                <>
                                    <AppleOutlined />
                                    <span>Apple</span>
                                </>
                            }
                            artistName={artist.key_name}
                            lastRelease={lastRelease}
                            profileUrl={appleMusicArtistUrl(appleId)}
                        />
                    )}
                </div>
            )}
            {!hasStoreRow && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    Latest release: {lastRelease}
                </Text>
            )}
        </div>
    );
};

export default ArtistSearchOption;
